import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useRef, useState } from "react";
import { ThumbsUpIcon, ThumbsDownIcon, XIcon, SendIcon } from "lucide-react";
import { cva } from "class-variance-authority";
import { cn } from "../../../lib/utils";
import { CopyButton, TipIconButton } from "../../../components/Button";
import { Button, Input } from "../../../components/base";
import { useUiTranslation } from "../../../i18n";
import { Popover, PopoverContent, PopoverTrigger, } from "../overlays/PortalPopover";
const MAX_FEEDBACK_CONTENT_LENGTH = 500;
export const ActionBar = ({ className, history, message, onHelpfulFeedback, onImproveFeedback, onFeedbackBlocked, extraActions, }) => {
    const { t } = useUiTranslation();
    const [feedbackBoxOpen, setFeedbackBoxOpen] = useState(false);
    const [feedbackContent, setFeedbackContent] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const canSubmitFeedback = typeof message.id === "number" &&
        Number.isFinite(message.id) &&
        message.id > 0;
    // Synchronous lock to prevent double submit before re-render
    const submitLockRef = useRef(false);
    // Accessibility: unique id for character count
    const feedbackCountId = useId();
    const handleThumbsUp = async () => {
        if (!canSubmitFeedback)
            return;
        if (submitLockRef.current)
            return;
        if (message.helpfulSubmitted) {
            onFeedbackBlocked?.("helpfulAlreadySubmitted");
            return;
        }
        submitLockRef.current = true;
        setIsSubmitting(true);
        setSubmitError(null);
        console.debug("Helpful feedback", history.id, message.id);
        try {
            await onHelpfulFeedback?.();
        }
        catch (error) {
            console.error("Failed to submit helpful feedback:", error);
            setSubmitError(t("chat.feedback.submitFailed", "Failed to submit feedback. Please try again."));
        }
        finally {
            submitLockRef.current = false;
            setIsSubmitting(false);
        }
    };
    const handleSubmitImprove = async (content) => {
        if (!canSubmitFeedback)
            return;
        if (submitLockRef.current)
            return;
        submitLockRef.current = true;
        setIsSubmitting(true);
        setSubmitError(null);
        console.debug("Improve feedback", history.id, message.id, content);
        try {
            const success = await onImproveFeedback?.(content);
            if (success) {
                setFeedbackBoxOpen(false);
                setFeedbackContent("");
            }
        }
        catch (error) {
            console.error("Failed to submit improve feedback:", error);
            setSubmitError(t("chat.feedback.submitFailed", "Failed to submit feedback. Please try again."));
        }
        finally {
            submitLockRef.current = false;
            setIsSubmitting(false);
        }
    };
    const handleFeedbackSubmit = async () => {
        await handleSubmitImprove(feedbackContent.trim() || undefined);
    };
    const handleFeedbackClose = async () => {
        // X button closes and submits feedback without content
        await handleSubmitImprove(undefined);
    };
    return (_jsxs("div", { className: cn("w-full flex flex-wrap items-center justify-start gap-0 mt-2 rounded-lg border-0", className), children: [_jsx(TipIconButton, { tip: t("chat.feedback.thumbsUp", "This answer is good"), button: _jsx("div", { className: "transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100 rounded-sm", children: _jsx(Button, { size: "icon", variant: "ghost", onClick: handleThumbsUp, disabled: !canSubmitFeedback || isSubmitting, className: "border-0 size-8 bg-transparent", children: _jsx(ThumbsUpIcon, { className: thumbsIconVariants({
                                active: message.helpfulSubmitted,
                            }) }) }) }) }), _jsxs(Popover, { open: feedbackBoxOpen, onOpenChange: (open) => {
                    if (!open) {
                        setFeedbackBoxOpen(false);
                        return;
                    }
                    if (isSubmitting)
                        return;
                    if (!canSubmitFeedback)
                        return;
                    if (message.improveSubmitted) {
                        onFeedbackBlocked?.("improveAlreadySubmitted");
                        return;
                    }
                    setFeedbackBoxOpen(true);
                }, children: [_jsx(TipIconButton, { tip: t("chat.feedback.improveTitle", "Help improve the AI"), button: _jsx(PopoverTrigger, { asChild: true, children: _jsxs(Button, { variant: "ghost", disabled: !canSubmitFeedback || isSubmitting, className: "border-0 h-8 px-0 has-[>svg]:px-2 bg-transparent rounded-sm transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100", children: [_jsx(ThumbsDownIcon, { className: thumbsIconVariants({
                                            active: message.improveSubmitted,
                                        }) }), _jsx("span", { className: "text-sm", children: t("chat.feedback.improve", "Improve") })] }) }) }), _jsxs(PopoverContent, { side: "bottom", align: "start", sideOffset: 6, collisionPadding: 8, className: "w-[min(320px,calc(100vw-1rem))] p-3 rounded-lg shadow-md", children: [_jsxs("div", { className: "flex items-center justify-between gap-2 mb-2", children: [_jsx("h3", { className: "text-sm font-medium text-foreground leading-tight", children: t("chat.feedback.improveTitle", "Help improve the AI") }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("span", { id: feedbackCountId, className: "text-xs text-muted-foreground", children: ["(", MAX_FEEDBACK_CONTENT_LENGTH - feedbackContent.length, "/", MAX_FEEDBACK_CONTENT_LENGTH, ")"] }), _jsx(Button, { size: "icon", variant: "ghost", title: t("chat.close", "Close"), "aria-label": t("chat.close", "Close"), disabled: !canSubmitFeedback || isSubmitting, onClick: handleFeedbackClose, className: "size-6 shrink-0", children: _jsx(XIcon, { className: "h-3.5 w-3.5" }) })] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Input, { type: "text", placeholder: t("chat.feedback.placeholder", "Describe the issue or suggestion..."), value: feedbackContent, onChange: (e) => setFeedbackContent(e.target.value.slice(0, MAX_FEEDBACK_CONTENT_LENGTH)), onKeyDown: (e) => {
                                            if (e.key === "Enter" &&
                                                !e.nativeEvent.isComposing &&
                                                !isSubmitting) {
                                                handleFeedbackSubmit();
                                            }
                                        }, disabled: !canSubmitFeedback || isSubmitting, className: "flex-1 h-8 px-3 text-sm", "aria-describedby": feedbackCountId, autoFocus: true }), _jsx(Button, { variant: "outline", onClick: handleFeedbackSubmit, disabled: !canSubmitFeedback || isSubmitting, className: "size-8 shrink-0", children: _jsx(SendIcon, { className: "h-4 w-4" }) })] })] })] }), _jsx(TipIconButton, { tip: t("chat.copy", "Copy"), button: _jsx("div", { className: "transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100 rounded-sm", children: _jsx(CopyButton, { content: message.content, className: "border-0 shadow-none size-8" }) }) }), extraActions, submitError ? (_jsx("p", { role: "alert", className: "w-full mt-1 text-xs text-destructive", children: submitError })) : null] }));
};
const thumbsIconVariants = cva("h-4 w-4 transition-colors", {
    variants: {
        active: {
            true: "fill-black dark:fill-white stroke-none",
            false: "fill-none stroke-current",
        },
    },
    defaultVariants: {
        active: false,
    },
});
//# sourceMappingURL=ActionBar.js.map